"use client"

import React from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Partner } from '../../../types/partner.type'

const partnerSchema = z.object({
  mode: z.enum(['new', 'existing']).default('new'),
  existingOrganizationId: z.string().nullable().optional().or(z.literal('')),
  name: z.string().min(1, 'Organization name is required').or(z.literal('')).optional(),
  contactPerson: z.string().nullable().optional().or(z.literal('')),
  phoneNumber: z.string().nullable().optional().or(z.literal('')),
  email: z.string().email('Invalid email address').nullable().optional().or(z.literal('')),
  specialties: z.string().nullable().optional().or(z.literal('')),
  address: z.string().nullable().optional().or(z.literal('')),
  city: z.string().nullable().optional().or(z.literal('')),
  state: z.string().nullable().optional().or(z.literal('')),
  zipCode: z.string().nullable().optional().or(z.literal('')),
  websiteUrl: z.string().url('Invalid URL').nullable().optional().or(z.literal('')),
  giveLink: z.string().url('Invalid URL').nullable().optional().or(z.literal('')),
  generalNotes: z.string().nullable().optional().or(z.literal('')),
  isActive: z.boolean().default(true),
  regionalContactPerson: z.string().nullable().optional().or(z.literal('')),
  regionalContactPhone: z.string().nullable().optional().or(z.literal('')),
  regionalContactEmail: z.string().email('Invalid email address').nullable().optional().or(z.literal('')),
  regionalIsActive: z.boolean().default(true),
  regionAssociationId: z.number().nullable().optional(),
}).superRefine((data, ctx) => {
  if (data.mode === 'new') {
    if (!data.name || data.name.trim().length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['name'],
        message: 'Organization name is required',
      })
    }
  } else {
    if (!data.existingOrganizationId || data.existingOrganizationId.trim().length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['existingOrganizationId'],
        message: 'Please select an existing organization',
      })
    }
  }
})

type PartnerFormData = z.infer<typeof partnerSchema>

interface PartnerFormProviderProps {
  children: React.ReactNode
  defaultValues?: Partial<PartnerFormData>
  onSubmit: (data: PartnerFormData) => Promise<void>
}

export default function PartnerFormProvider({ 
  children, 
  defaultValues = { isActive: true },
  onSubmit 
}: PartnerFormProviderProps) {
  // Transform null values to empty strings for form compatibility
  const transformedDefaults: PartnerFormData = defaultValues ? {
    ...defaultValues,
    mode: (defaultValues.mode as 'new' | 'existing') ?? 'new',
    existingOrganizationId: defaultValues.existingOrganizationId ?? '',
    name: defaultValues.name ?? '',
    contactPerson: defaultValues.contactPerson ?? '',
    phoneNumber: defaultValues.phoneNumber ?? '',
    email: defaultValues.email ?? '',
    specialties: defaultValues.specialties ?? '',
    address: defaultValues.address ?? '',
    city: defaultValues.city ?? '',
    state: defaultValues.state ?? '',
    zipCode: defaultValues.zipCode ?? '',
    websiteUrl: defaultValues.websiteUrl ?? '',
    giveLink: defaultValues.giveLink ?? '',
    generalNotes: defaultValues.generalNotes ?? '',
    isActive: defaultValues.isActive ?? true,
    regionalContactPerson: defaultValues.regionalContactPerson ?? '',
    regionalContactPhone: defaultValues.regionalContactPhone ?? '',
    regionalContactEmail: defaultValues.regionalContactEmail ?? '',
    regionalIsActive: defaultValues.regionalIsActive ?? true,
    regionAssociationId: defaultValues.regionAssociationId ?? undefined,
  } : { mode: 'new', isActive: true, regionalIsActive: true }

  const form = useForm<PartnerFormData>({
    resolver: zodResolver(partnerSchema),
    defaultValues: transformedDefaults,
    mode: "onChange",
  })

  const { handleSubmit, formState: { errors, isSubmitting } } = form

  const handleFormSubmit = async (data: PartnerFormData) => {
    // Transform empty strings to undefined for backend validation
    const transformedData = {
      ...data,
      name: data.name?.trim() ? data.name : undefined,
      existingOrganizationId: data.existingOrganizationId?.trim() ? data.existingOrganizationId : undefined,
      contactPerson: data.contactPerson?.trim() ? data.contactPerson : null,
      phoneNumber: data.phoneNumber?.trim() ? data.phoneNumber : null,
      email: data.email?.trim() ? data.email : null,
      specialties: data.specialties?.trim() ? data.specialties : null,
      address: data.address?.trim() ? data.address : null,
      city: data.city?.trim() ? data.city : null,
      state: data.state?.trim() ? data.state : null,
      zipCode: data.zipCode?.trim() ? data.zipCode : null,
      websiteUrl: data.websiteUrl?.trim() ? data.websiteUrl : null,
      giveLink: data.giveLink?.trim() ? data.giveLink : null,
      generalNotes: data.generalNotes?.trim() ? data.generalNotes : null,
      regionalContactPerson: data.regionalContactPerson?.trim() ? data.regionalContactPerson : null,
      regionalContactPhone: data.regionalContactPhone?.trim() ? data.regionalContactPhone : null,
      regionalContactEmail: data.regionalContactEmail?.trim() ? data.regionalContactEmail : null,
      regionalIsActive: data.regionalIsActive,
      regionAssociationId: data.regionAssociationId ?? undefined,
    }
    
    await onSubmit(transformedData)
  }

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(handleFormSubmit)} noValidate>
        {children}
      </form>
    </FormProvider>
  )
}
